import { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import API from '../config/axiosConfig';
import { getDepartmentNameErrors } from '../utils/department-utils';
import InputWithIcon from './InputWithIcon';
import LoadingSubmitButton from './LoadingSubmitButton';
import CustomDropdown from './CustomDropdown';
import DepartmentSelectionStepSkeleton from './DepartmentSelectionStepSkeleton';
import './DepartmentSelectionStep.scss';

const DepartmentSelectionStep = ({
  selectedHospital,
  departmentOption,
  onDepartmentOptionChange,
  selectedDepartment,
  onSelectedDepartmentChange,
  newDepartment,
  onNewDepartmentChange,
  onSubmit,
  isSubmitting = false,
  error = '',
  formErrors = {},
  showSubmitButton = true,
  submitButtonText = 'אישור',
  submitLoadingText = 'מבצע...',
  // New props for navigation support
  showBackButton = false,
  onBack = null,
  backButtonText = 'חזור',
  showNextButton = false,
  departmentIcon,
  noDepartmentsMessage = 'אין מחלקות זמינות עם מנהלים. אנא צור מחלקה חדשה.',
  onDuplicateNameChange = null
}) => {
  const [departments, setDepartments] = useState([]);
  // Every department of the hospital, including those without admins - a new name must not
  // collide with any of them.
  const [allDepartments, setAllDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load departments when hospital is selected
  useEffect(() => {
    if (selectedHospital) {
      const fetchDepartments = async () => {
        setIsLoading(true);
        try {
          const response = await API.get(`/api/onboarding/hospitals/${selectedHospital}/departments`);
          const departmentsWithAdmins = response.data.filter(dept => dept.admins && dept.admins.length > 0);
          setAllDepartments(response.data);
          setDepartments(departmentsWithAdmins);

          if (departmentsWithAdmins.length === 0) {
            onDepartmentOptionChange('create');
          } else if (!departmentOption) {
            onDepartmentOptionChange('join');
          }
        } catch (err) {
          console.error('Failed to fetch departments:', err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchDepartments();
    }
  }, [selectedHospital]); // Removed onDepartmentOptionChange from dependencies

  // A department name that is already taken in this hospital is rejected by the server, so flag it
  // as soon as it is typed.
  const { nameHebrew: newNameHebrew, nameEnglish: newNameEnglish } = newDepartment;
  const duplicateErrors = useMemo(
    () => (departmentOption === 'create'
      ? getDepartmentNameErrors(allDepartments, { nameHebrew: newNameHebrew, nameEnglish: newNameEnglish })
      : {}),
    [allDepartments, departmentOption, newNameHebrew, newNameEnglish]
  );
  const hasDuplicateName = Object.keys(duplicateErrors).length > 0;

  // Let the parent block its own submit button on the same condition.
  const onDuplicateNameChangeRef = useRef(onDuplicateNameChange);
  onDuplicateNameChangeRef.current = onDuplicateNameChange;
  const duplicateKey = `${duplicateErrors.nameHebrew || ''}|${duplicateErrors.nameEnglish || ''}`;
  useEffect(() => {
    onDuplicateNameChangeRef.current?.(duplicateErrors);
  }, [duplicateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldErrors = { ...formErrors, ...duplicateErrors };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasDuplicateName) {
      return;
    }
    onSubmit();
  };

  return (
    <form className="department-selection-step" onSubmit={handleSubmit} style={{ textAlign: 'right', direction: 'rtl' }}>
      {isLoading ? (
        <DepartmentSelectionStepSkeleton />
      ) : departments.length > 0 ? (
        <div>
          <div style={{ fontStyle: 'italic', marginBottom: '10px' }}>
            יש באפשרותך להצטרף לאחת המחלקות שכבר עובדות עם mashlom.me, או ליצור מחלקה חדשה אליה ניתן להוסיף את חברי המחלקה שלך.
          </div>
          <div className="radio-group" style={{ textAlign: 'right' }}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block'}}>
                <input
                  type="radio"
                  name="departmentOption"
                  value="join"
                  checked={departmentOption === 'join'}
                  onChange={(e) => onDepartmentOptionChange(e.target.value)}
                  style={{ marginLeft: '8px' }}
                  data-testid="department-join-radio"
                />
                הצטרף למחלקה קיימת
              </label>
              <div className="join-section" style={{ textAlign: 'right', marginRight: '20px', opacity: departmentOption !== 'join' ? 0.5 : 1, pointerEvents: departmentOption !== 'join' ? 'none' : 'auto' }}>
                <p >לאחר האישור, יישלח אימייל למנהלי המחלקה בבקשה לאשר את הצטרפותך למחלקה</p>
                <CustomDropdown
                  placeholder="בחר מחלקה"
                  selected={selectedDepartment}
                  onSelect={(option) => onSelectedDepartmentChange(option.value)}
                  options={departments.map(dept => ({
                    value: dept._id,
                    label: dept.nameHebrew,
                    sublabel: `מנהלים: ${dept.admins.map(a => a.name).join(', ')}`
                  }))}
                />
                {fieldErrors.selectedDepartment && <div className="error-message">{fieldErrors.selectedDepartment}</div>}
              </div>
            </div>

            <div >
              <label style={{ display: 'block'}}>
                <input
                  type="radio"
                  name="departmentOption"
                  value="create"
                  checked={departmentOption === 'create'}
                  onChange={(e) => onDepartmentOptionChange(e.target.value)}
                  style={{ marginLeft: '8px' }}
                  data-testid="department-create-radio"
                />
                צור מחלקה חדשה
              </label>
              <div className="create-section" style={{ textAlign: 'right', marginRight: '20px', opacity: departmentOption !== 'create' ? 0.5 : 1, pointerEvents: departmentOption !== 'create' ? 'none' : 'auto' }}>
                <div>
                  <InputWithIcon
                    icon={departmentIcon}
                    type="text"
                    name="nameHebrew"
                    placeholder="שם המחלקה בעברית"
                    value={newDepartment.nameHebrew}
                    onChange={(e) => onNewDepartmentChange('nameHebrew', e.target.value)}
                    disabled={departmentOption !== 'create' && departments.length > 0}
                    error={fieldErrors.nameHebrew}
                  />
                  {fieldErrors.nameHebrew && <div className="error-message">{fieldErrors.nameHebrew}</div>}
                </div>
                <div style={{height: '10px'}}></div>
                <div>
                  <InputWithIcon
                    icon={departmentIcon}
                    type="text"
                    name="nameEnglish"
                    placeholder="שם המחלקה באנגלית"
                    value={newDepartment.nameEnglish}
                    onChange={(e) => onNewDepartmentChange('nameEnglish', e.target.value)}
                    disabled={departmentOption !== 'create' && departments.length > 0}
                    error={fieldErrors.nameEnglish}
                  />
                  {fieldErrors.nameEnglish && <div className="error-message">{fieldErrors.nameEnglish}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-departments-message" style={{ textAlign: 'right' }}>
          <p style={{ marginBottom: '10px' }}>{noDepartmentsMessage}</p>
          <div className="create-section" style={{ textAlign: 'right' }}>
            <div>
              <InputWithIcon
                icon={departmentIcon}
                type="text"
                name="nameHebrew"
                placeholder="שם המחלקה בעברית"
                value={newDepartment.nameHebrew}
                onChange={(e) => onNewDepartmentChange('nameHebrew', e.target.value)}
                error={fieldErrors.nameHebrew}
              />
              {fieldErrors.nameHebrew && <div className="error-message">{fieldErrors.nameHebrew}</div>}
            </div>
            <div style={{height: '10px'}}></div>
            <div>
              <InputWithIcon
                icon={departmentIcon}
                type="text"
                name="nameEnglish"
                placeholder="שם המחלקה באנגלית"
                value={newDepartment.nameEnglish}
                onChange={(e) => onNewDepartmentChange('nameEnglish', e.target.value)}
                error={fieldErrors.nameEnglish}
              />
              {fieldErrors.nameEnglish && <div className="error-message">{fieldErrors.nameEnglish}</div>}
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      
      {/* Navigation buttons */}
      {(showSubmitButton || showBackButton || showNextButton) && (
        <div className="wizard-buttons">
          {/* Back button */}
          {showBackButton && onBack && (
            <button
              type="button"
              onClick={onBack}
              className="back-button"
              disabled={isSubmitting}
              style={{ opacity: isSubmitting ? 0.5 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
            >
              {backButtonText}
            </button>
          )}

          {/* Submit button (for final step or standalone usage) */}
          {showSubmitButton && (
            <LoadingSubmitButton
              className='onboarding-button'
              isLoading={isSubmitting}
              text={submitButtonText}
              loadingText={submitLoadingText}
              type="submit"
            />
          )}
        </div>
      )}
    </form>
  );
};

DepartmentSelectionStep.propTypes = {
  selectedHospital: PropTypes.string,
  departmentOption: PropTypes.string,
  onDepartmentOptionChange: PropTypes.func.isRequired,
  selectedDepartment: PropTypes.string,
  onSelectedDepartmentChange: PropTypes.func.isRequired,
  newDepartment: PropTypes.shape({
    nameHebrew: PropTypes.string,
    nameEnglish: PropTypes.string,
  }).isRequired,
  onNewDepartmentChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  error: PropTypes.string,
  formErrors: PropTypes.object,
  showSubmitButton: PropTypes.bool,
  submitButtonText: PropTypes.string,
  submitLoadingText: PropTypes.string,
  showBackButton: PropTypes.bool,
  onBack: PropTypes.func,
  backButtonText: PropTypes.string,
  showNextButton: PropTypes.bool,
  departmentIcon: PropTypes.string,
  noDepartmentsMessage: PropTypes.string,
  onDuplicateNameChange: PropTypes.func,
};

export default DepartmentSelectionStep;
